﻿import { useEffect, useState } from 'react';
import * as React from 'react';

import { GMetrixProgressBar } from 'components/GMetrixProgressBar/GMetrixProgressBar';
import { GenerateReport } from 'components/GenerateReport/GenerateReport';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Button, Col, Container, Modal, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Student, StudentApi } from 'apis/StudentApi';
import { ResultItem } from 'apis/ReportApi';

import 'components/Transcript/transcript.scss';

interface TranscriptProps {
    student?: Student;
    studentId: number;
}

export interface Transcript {
    Assignments: TranscriptTransaction[];
    EmailAddress?: string;
    FirstName: string;
    LastName: string;
    StudentID: number;
    TodaysDateString: string;
    UserName: string;
}

interface TranscriptTransaction {
    Category: TranscriptCategory[];
    Code: string;
    ContentAccessID: number;
    CreatedBy: string;
    EnrolledDate: string;
    EnrolledDateString: string;
    ExpirationDate: string;
    ExpirationDateString: string;
    PartnerLabel: string;
    PartnerName: string;
    TransactionName: string;
}

interface TranscriptCategory {
    CategoryID: number;
    CategoryName: string;
    Product: TranscriptProduct[];
}

interface TranscriptProduct {
    Courses: TranscriptCourse[];
    ProductID: number;
    ProductName: string;
    Tests: TranscriptTest[];
}

interface TranscriptCourse {
    CompletedDate?: string;
    CompletedDateString?: string;
    CourseName: string;
    CourseStudentID: number;
    Progress?: number;
    Publisher: string;
    SecondsSpent: number;
    StartDate: string;
    StartDateString?: string;
}

interface TranscriptTest {
    ElapsedTime: string;
    FinishDate?: string;
    FinishDateString?: string;
    PassFail: string;
    PassingScore: number;
    ResultID: number;
    Score: number;
    StartDate: string;
    StartDateString?: string;
    TestName: string;
    TestStatus: string;
    TestType: string;
}

export function Transcript({ student, studentId }: TranscriptProps) {

    const [transcript, setTranscript] = useState<Transcript | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [currentPracticeTestResultId, setCurrentPracticeTestResultId] = useState<number | null>(null);
    const { formatMessage } = useIntl();

    useEffect(() => {

        StudentApi.GetTranscript(studentId)
            .then(transcript => {

                setTranscript(transcript);
                setLoading(false);

            })
            .catch(error => transcriptFailure(error.Message));

    }, []);

    // Probably the worst way possible, but needed something quick.
    const toHHMMSS = (secs: number) => {

        let seconds = parseInt(secs.toString(), 10).toString(); // don't forget the second param
        let hours = Math.floor(parseInt(seconds) / 3600).toString();
        let minutes = Math.floor((parseInt(seconds) - parseInt(hours) * 3600) / 60).toString();
        seconds = (parseInt(seconds) - parseInt(hours) * 3600 - parseInt(minutes) * 60).toString();

        if (parseInt(hours) < 10) {

            hours = '0' + hours;

        }
        if (parseInt(minutes) < 10) {

            minutes = '0' + minutes;

        }
        if (parseInt(seconds) < 10) {

            seconds = '0' + seconds;

        }

        const time = hours + ':' + minutes + ':' + seconds;
        return time;

    };

    // Tests are scored out of 100 or 1000; the passing score shares the test's scale
    const getTestPercentage = (test: TranscriptTest) => {

        const score = test.Score ?? 0;
        const maxScore = score > 100 || test.PassingScore > 100 ? 1000 : 100;

        return Math.round(score / maxScore * 100);

    };

    const transcriptFailure = (message: string) => {

        setErrorMessage(`${formatMessage({ id: 'this_student_has_not_started_any_content.ab4e7311', defaultMessage: 'This student has not started any Content, so there is no data to show.' })}`);
        setLoading(false);

    };

    const openTestResultsOnStudentPanel = (practiceTestResultId: number) => {

        setCurrentPracticeTestResultId(practiceTestResultId);
        setShowModal(true);

    };

    const closeModal = () => {

        setShowModal(false);
        setCurrentPracticeTestResultId(null);

    };

    return (
        <>
            <div id="transcriptContainer">
                {!loading && transcript ? (
                    <Container fluid className="transcript pb-4 pt-4">
                        <Row className="p-3 border-bottom">
                            <Col>
                                <Row>
                                    <Col className="transcript__student-name" lg={12}>
                                        {transcript.FirstName}
                                        {' '}
                                        {transcript.LastName}
                                    </Col>
                                    <Col lg={12}>
                                        {transcript.EmailAddress ? transcript.EmailAddress : transcript.UserName}
                                    </Col>
                                    <Col lg={12}>
                                        {transcript.TodaysDateString}
                                    </Col>
                                </Row>
                            </Col>
                            <Col className="d-flex justify-content-end align-items-center">
                                <img
                                    alt={formatMessage({ id: 'gmetrixlogo.086c64e5', defaultMessage: 'GMetrixLogo' })}
                                    src="https://content.gmetrix.net/images/GMetrixLogoBlue.png"
                                    width="150px"
                                />
                            </Col>
                        </Row>
                        {transcript.Assignments.map((transaction: TranscriptTransaction) => (
                            <>
                                <Row className="mt-3 mb-1 transcript__transaction-header mt-4">
                                    <Col>
                                        <FormattedMessage defaultMessage="Access Code" id="access_code.b5fc1b57" />
                                        :
                                        {' '}
                                        {transaction.TransactionName}
                                    </Col>
                                    <Col>
                                        <FormattedMessage defaultMessage="Date Assigned" id="date_assigned.052e4beb" />
                                        :
                                        {' '}
                                        {transaction.EnrolledDateString}
                                    </Col>
                                    <Col>
                                        <FormattedMessage defaultMessage="Institution" id="institution.a9dd083a" />
                                        :
                                        {' '}
                                        {transaction.PartnerLabel}
                                    </Col>
                                    <Col>
                                        <FormattedMessage defaultMessage="Assigned By" id="assigned_by.eef8f660" />
                                        :
                                        {' '}
                                        {transaction.CreatedBy}
                                    </Col>
                                    <Col>
                                        <FormattedMessage defaultMessage="Due Date" id="due_date.712b37be" />
                                        :
                                        {' '}
                                        {transaction.ExpirationDateString}
                                    </Col>
                                </Row>
                                {transaction.Category.map((category: TranscriptCategory, i: number) => (
                                    <>
                                        <Row
                                            className={`transcript__transaction-container mx-2 pt-2 ${i === 0 && 'rounded-top'
                                            } ${i === transaction.Category.length - 1 && 'rounded-bottom'}`}
                                        >
                                            <Col className="py-3 ps-4" lg={12}>
                                                {category.CategoryName}
                                            </Col>
                                            {category.Product.map((product: TranscriptProduct) => (
                                                <>
                                                    <Col className="mx-2 py-3">
                                                        {product.ProductName}
                                                    </Col>
                                                    <Col className="transcript__transaction-resource-container" lg={12}>
                                                        <Row className="mx-2 p-2 border-bottom transcript__list-item-header">
                                                            <Col className="font-weight-bold" lg={4}>
                                                                <FormattedMessage defaultMessage="Name" id="name.5458f379" />
                                                            </Col>
                                                            <Col>
                                                                <FormattedMessage defaultMessage="Start Date" id="start_date.66ba5b72" />
                                                            </Col>
                                                            <Col>
                                                                <FormattedMessage defaultMessage="Completed Date" id="completed_date.3bff5c23" />
                                                            </Col>
                                                            <Col>
                                                                <FormattedMessage defaultMessage="Score/Progress" id="scoreprogress.dd89e714" />
                                                            </Col>
                                                            <Col>
                                                                <FormattedMessage defaultMessage="Time Spent" id="time_spent.da775c0c" />
                                                            </Col>
                                                        </Row>
                                                        {product.Tests &&
                                                            product.Tests.map(test => (
                                                                <>
                                                                    <Row className="mx-2 p-2 border-bottom transcript__list-item-content">
                                                                        <Col
                                                                            onClick={() => test.FinishDate &&
                                                                                openTestResultsOnStudentPanel(test.ResultID)
                                                                            }
                                                                            style={{
                                                                                cursor: test.FinishDate && 'pointer'
                                                                            }}
                                                                            lg={4}
                                                                        >
                                                                            <i className="fa-solid fa-file-text me-2 mb-1"></i>
                                                                            {test.TestName}
                                                                        </Col>
                                                                        <Col>
                                                                            {test.StartDateString}
                                                                        </Col>
                                                                        <Col>
                                                                            {test.FinishDateString
                                                                                ? test.FinishDateString
                                                                                : '—'}
                                                                        </Col>
                                                                        <Col>
                                                                            <GMetrixProgressBar
                                                                                dataItem={{
                                                                                    Answered: false,
                                                                                    Color: '#00A38D',
                                                                                    Date: '',
                                                                                    Description: '',
                                                                                    QuestionName: '',
                                                                                    Share: String(getTestPercentage(test)),
                                                                                    Title: ''
                                                                                } as ResultItem}
                                                                            />
                                                                        </Col>
                                                                        <Col>
                                                                            {test.ElapsedTime
                                                                                ? test.ElapsedTime
                                                                                : '00.00.00'}
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            ))}

                                                        {product.Courses &&
                                                            product.Courses.map((course, index) => (
                                                                <Row className="mx-2 p-2 border-bottom transcript__list-item-content" key={index}>
                                                                    <Col lg={4}>
                                                                        <i className="fa-solid fa-window-maximize me-2 mb-1"></i>
                                                                        {product.ProductName}
                                                                    </Col>
                                                                    <Col>
                                                                        {course.StartDateString}
                                                                    </Col>
                                                                    <Col>
                                                                        {course.CompletedDateString
                                                                            ? course.CompletedDateString
                                                                            : '—'}
                                                                    </Col>
                                                                    <Col>
                                                                        {course.Progress ? (
                                                                            <>
                                                                                <i
                                                                                    className="fa-solid fa-check-circle text-success me-1"
                                                                                    style={{ color: '#00A38D' }}
                                                                                ></i>
                                                                                {course.Progress.toFixed(2)}
                                                                                %
                                                                            </>
                                                                        ) : (
                                                                            '—'
                                                                        )}
                                                                    </Col>
                                                                    <Col>
                                                                        {toHHMMSS(course.SecondsSpent)
                                                                            ? toHHMMSS(course.SecondsSpent)
                                                                            : '00.00.00'}
                                                                    </Col>
                                                                </Row>
                                                            ))}
                                                    </Col>
                                                </>
                                            ))}
                                        </Row>
                                    </>
                                ))}
                            </>
                        ))}
                    </Container>
                ) : (
                    <Container>
                        {!loading && student && (
                            <Row className="p-3 border-bottom">
                                <Col>
                                    <Row>
                                        <Col className="transcript__student-name" lg={12}>
                                            {student.Firstname}
                                            {' '}
                                            {student.Lastname}
                                        </Col>
                                        <Col lg={12}>
                                            {student.Email ? student.Email : student.Username}
                                        </Col>
                                    </Row>
                                </Col>
                                <Col className="d-flex justify-content-end align-items-center">
                                    <img
                                        alt={formatMessage({ id: 'gmetrixlogo.086c64e5', defaultMessage: 'GMetrixLogo' })}
                                        src="https://content.gmetrix.net/images/GMetrixLogoBlue.png"
                                        width="150px"
                                    />
                                </Col>
                            </Row>
                        )}
                        <Row>
                            <Col className="d-flex justify-content-center">
                                {loading && <GMetrixLoader/>}
                                {errorMessage.length > 0 && (
                                <p className="error-message rounded">
                                    {errorMessage}
                                </p>
                                )}
                            </Col>
                        </Row>
                    </Container>
                )}
            </div>

            <Modal onHide={closeModal} show={showModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FormattedMessage defaultMessage="Test Result Details" id="test_result_details.21fe3b90" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {currentPracticeTestResultId && (
                        <GenerateReport
                            showNoFilters
                            autoRunReport={true}
                            reportId={60}
                            scopeId={10}
                            scopeObjectId={currentPracticeTestResultId}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={closeModal} variant="outline-secondary">
                        <FormattedMessage defaultMessage="Close" id="close.99e01fb5" />
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );

}
